import { CoreAdminContext, Resource, CoreAdminUI, CustomRoutes } from "ra-core";
import dataProvider from "./dataProvider";
import { Layout, Login } from "./layout";
import authProvider from "./authProvider";
import englishMessages from "./i18n/en";
import polyglotI18nProvider from "ra-i18n-polyglot";
import { ThemeProvider } from "./layout/ThemeProvider";
import { Route } from "react-router-dom";

// resources

// core resources
import ServiceLocationsList from "./core-modules/service-locations/ServiceLocationsList";

// core custom routes
import Settings from "./core-modules/settings/Settings";
import Support from "./core-modules/support/Support";

// resources for dispatch
import TaxiOrderList from "./taxi-dispatch/orders/List";
import FixedRoutesList from "./taxi-dispatch/fixed-routes/List";

// custom routes for tours agent
import Reports from "./tours-agent/reports/Reports";

// resources for rental
import RentalOrderList from "./rental/orders/OrdersList";
import NotificationsList from "./core-modules/notifications/List";
import RentalReservationCalendar from "./rental/calendar/reservations-calendar";
import Dispatch from "./taxi-dispatch/dispatch/Dispatch";
import OrderReservations from "./taxi-dispatch/orders/Reservations";
import Dashboard from "./dashboard";
import ServiceReservations from "./garageManagment/reservations/Reservations";
import Invoice from "./garageManagment/invoice/Invoice";
import VendorsList from "./core-modules/vendors/VendorsList";
import UserRolesList from "./managment/userRoles/List";
import VehicleClassList from "./rental/vehicle_classes/VehicleClassList";
import RentalPricingList from "./rental/pricing/RentalPricingList";
import { BrowserRouter } from "react-router-dom";
import OrdersList from "./tours-agent/orders/OrdersList";
import MyOrdersList from "./tours-agent/myorders/OrdersList";
import TransfersList from "./tours-agent/transfers/TransfersList";
import ToursList from "./tours-agent/tours/ToursList";
import TravelReservations from "./tours-agent/reservations/Reservations";
import UsersList from "./core-modules/users/UsersList";
import InventoryImport from "./garageManagment/invetory/Import";
import ForgotPass from "./layout/ForgotPass";
import RoleBasedContextProvider from "./context/RoleBasedContextProvider";
import RouteAreaRatesList from "./taxi-dispatch/route-area-rates/RouteAreaRatesList";
import ClientsList from "./core-modules/clients/ClientsList";
import { UserProvider, useUserContext } from "./context/UserContext";
import DriversList from "./core-modules/drivers/List";
import CoreDevicesList from "./core-modules/devices/List";
import VehiclesList from "./core-modules/vehicles/VehiclesList";
import ServiceList from "./logistic/services/ServiceList";
import ExpensesList from "./logistic/expenses/ExpenseList";
import RefuelingList from "./logistic/refueling/RefuelingList";
import IncomesList from "./logistic/incomes/IncomesList";
import PartnersList from "./core-modules/partners/PartnersList";
import InvetoryList from "./garageManagment/invetory/List";
import BranchList from "./garageManagment/branches/List";
import AutoServicesList from "./garageManagment/services/List";
import AutoClientsList from "./garageManagment/clients/List";
import AutoVehiclesList from "./garageManagment/vehicle/List";
import NotFoundPage from "./components/error-pages/NotFoundPage";
import { AutomotiveLoader } from "./components/ui/automotive-loader";
import ReactAdminError from "./components/error-pages/ReactAdminError";
import CallLogs from "./taxi-dispatch/call-logs/CallLogs";
import TaxiReportData from "./taxi-dispatch/reports/TaxiReportData";

const i18nProvider = polyglotI18nProvider(
  (locale) => {
    if (locale === "sq") {
      return import("./i18n/sq").then((messages) => messages.default);
    }
    return englishMessages;
  },
  "en",
  [
    { locale: "en", name: "English" },
    { locale: "sq", name: "Albanian" },
  ],
);

// Move configuration outside component to prevent recreation on each render
const resourceConfig: Record<
  string,
  Record<string, Array<{ name?: string; [key: string]: unknown }>>
> = {
  taxi: {
    operator: [
      { name: "taxi-orders", list: <TaxiOrderList /> },
      {
        name: "users",
        list: (
          <UsersList
            roles={[
              { label: "Owner", value: "owner" },
              { label: "Operator", value: "operator" },
            ]}
          />
        ),
      },
      { name: "reservations", list: <OrderReservations /> },
      { name: "drivers", list: <DriversList /> },
      { name: "devices", list: <CoreDevicesList /> },
      { name: "vehicles", list: <VehiclesList /> },
      { name: "clients", list: <ClientsList /> },
      { name: "call-logs", list: <CallLogs /> },
    ],
    commission_partner: [
      { name: "taxi-orders", list: <TaxiOrderList /> },
      {
        name: "users",
        list: (
          <UsersList
            roles={[
              { label: "Owner", value: "owner" },
              { label: "Operator", value: "operator" },
            ]}
          />
        ),
      },
    ],
    owner: [
      { name: "taxi-orders", list: <TaxiOrderList /> },
      { name: "reports", list: <TaxiReportData /> },
      { name: "call-logs", list: <CallLogs /> },
      { name: "drivers", list: <DriversList /> },
      { name: "devices", list: <CoreDevicesList /> },
      { name: "fixed-routes", list: <FixedRoutesList /> },
      { name: "route-area-rates", list: <RouteAreaRatesList /> },
      { name: "referral-agents", list: <PartnersList /> },
      { name: "vehicles", list: <VehiclesList /> },
      {
        name: "users",
        list: (
          <UsersList
            roles={[
              { label: "Owner", value: "owner" },
              { label: "Operator", value: "operator" },
            ]}
          />
        ),
      },
      { name: "service", list: <ServiceList /> },
      { name: "expenses", list: <ExpensesList /> },
      { name: "refueling", list: <RefuelingList /> },
      { name: "incomings", list: <IncomesList /> },
      { name: "reservations", list: <OrderReservations /> },
      { name: "clients", list: <ClientsList /> },
      // { name: "clients-notifications", list: <ClientNotificationList /> },
    ],
  },
  rental: {
    commission_partner: [{ name: "reservations", list: <RentalOrderList /> }],
    owner: [
      { name: "reservations", list: <RentalOrderList /> },
      { name: "vehicles", list: <VehiclesList /> },
      { name: "referral-agents", list: <PartnersList /> },
      { name: "service", list: <ServiceList /> },
      { name: "expenses", list: <ExpensesList /> },
      { name: "refueling", list: <RefuelingList /> },
      { name: "incomings", list: <IncomesList /> },
      {
        name: "users",
        list: <UsersList roles={[{ label: "Owner", value: "owner" }]} />,
      },
    ],
  },
  transportation_agency: {
    commission_partner: [
      { name: "orders", list: MyOrdersList },
      { name: "reservations", list: TravelReservations },
      { name: "tours", list: ToursList },
      { name: "transfers", list: TransfersList },
    ],
    owner: [
      { name: "orders", list: OrdersList },
      { name: "reservations", list: TravelReservations },
      { name: "drivers", list: <DriversList /> },
      { name: "devices", list: <CoreDevicesList /> },
      { name: "referral-agents", list: <PartnersList /> },
      { name: "tours", list: ToursList },
      { name: "transfers", list: TransfersList },
      { name: "vehicles", list: <VehiclesList /> },
      {
        name: "users",
        list: (
          <UsersList
            roles={[
              { label: "Owner", value: "owner" },
              { label: "Operator", value: "operator" },
              { label: "Commision partner", value: "commision_partner" },
            ]}
          />
        ),
      },
    ],
  },
  auto_service: {
    owner: [
      { name: "reservation", list: ServiceReservations },
      { name: "invoices", list: Invoice },
      { name: "users", list: UserRolesList },
      { name: "vendors", list: VendorsList },
      { name: "inventory", list: InvetoryList },
      { name: "inventory/import", list: InventoryImport },
      { name: "branches", list: BranchList },
      { name: "services", list: AutoServicesList },
      { name: "client", list: AutoClientsList },
      { name: "vehicles", list: AutoVehiclesList },
    ],
  },
  logistics: {
    owner: [
      { name: "vehicles", list: <VehiclesList /> },
      {
        name: "users",
        list: (
          <UsersList
            roles={[
              { label: "Owner", value: "owner" },
              { label: "Operator", value: "operator" },
            ]}
          />
        ),
      },
      { name: "service", list: <ServiceList /> },
      { name: "expenses", list: <ExpensesList /> },
      { name: "refueling", list: <RefuelingList /> },
      { name: "incomings", list: <IncomesList /> },
    ],
  },
};

const customRoutesConfig: Record<string, Record<string, JSX.Element[]>> = {
  taxi: {
    operator: [
      <Route key="settings" path="/settings" element={<Settings />} />,
      <Route key="support" path="/support" element={<Support />} />,
    ],
    owner: [
      <Route key="dispatch" path="/dispatch" element={<Dispatch />} />,
      <Route
        key="service-locations"
        path="/service-locations"
        element={<ServiceLocationsList />}
      />,
      <Route
        key="route-area-rates"
        path="/route-area-rates"
        element={<RouteAreaRatesList />}
      />,

      // <Route key="reservations" path="/reservations" element={<DispatchReservations />} />,
      <Route key="settings" path="/settings" element={<Settings />} />,
      <Route key="support" path="/support" element={<Support />} />,
    ],
  },
  rental: {
    commission_partner: [
      <Route
        key="rentalOrders"
        path="/rental-orders"
        element={<RentalReservationCalendar />}
      />,
      <Route key="settings" path="/settings" element={<Settings />} />,
      <Route key="support" path="/support" element={<Support />} />,
    ],
    owner: [
      <Route key="gps-track" path="/gps-track" element={<Dispatch />} />,
      <Route
        key="rentalOrders"
        path="/rental-orders"
        element={<RentalReservationCalendar />}
      />,
      <Route key="reports" path="/reports" element={<Reports />} />,
      <Route key="settings" path="/settings" element={<Settings />} />,
      <Route key="support" path="/support" element={<Support />} />,
      <Route
        key="vehicle-classes"
        path="/vehicle-classes"
        element={<VehicleClassList />}
      />,
      <Route
        key="rental-pricing"
        path="/rental-pricing"
        element={<RentalPricingList />}
      />,
    ],
  },
  transportation_agency: {
    commission_partner: [
      <Route key="settings" path="/settings" element={<Settings />} />,
      <Route key="support" path="/support" element={<Support />} />,
    ],
    owner: [
      <Route key="reports" path="/reports" element={<Reports />} />,
      <Route key="settings" path="/settings" element={<Settings />} />,
      <Route key="support" path="/support" element={<Support />} />,
    ],
  },
  auto_service: {
    owner: [
      <Route key="settings" path="/settings" element={<Settings />} />,
      <Route key="support" path="/support" element={<Support />} />,
    ],
  },
  logistics: {
    owner: [
      <Route key="settings" path="/settings" element={<Settings />} />,
      <Route key="support" path="/support" element={<Settings />} />,
    ],
  },
};

// Move Content component outside to prevent recreation on each render
const Content = () => (
  <CoreAdminContext
    dataProvider={dataProvider}
    authProvider={authProvider}
    i18nProvider={i18nProvider}
  >
    <CoreAdminUI
      layout={Layout}
      dashboard={Dashboard}
      loginPage={Login}
      error={ReactAdminError}
    >
      <Resource name="notifications" list={<NotificationsList />} />
      <CustomRoutes noLayout>
        <Route key="forgot-pass" path="/forgot-pass" element={<ForgotPass />} />
      </CustomRoutes>
      {(permissions) => {
        const { settings_type = "taxi", user_role = "operator" } =
          permissions || {};
        const resources = resourceConfig[settings_type]?.[user_role] || [];
        const customRoutes =
          customRoutesConfig[settings_type]?.[user_role] || [];
        return [
          <CustomRoutes key="custom-routes">{customRoutes}</CustomRoutes>,
          ...resources.map(
            (
              resource: { name?: string; [key: string]: unknown },
              index: number,
            ) => (
              <Resource
                name={resource.name || `resource-${index}`}
                key={resource.name || index}
                {...resource}
              />
            ),
          ),
          <CustomRoutes key="not-found-route">
            <Route path="*" element={<NotFoundPage />} />
          </CustomRoutes>,
        ];
      }}
    </CoreAdminUI>
  </CoreAdminContext>
);

const App = () => {
  const { decodedToken, error, isLoading } = useUserContext();

  if (error) {
    console.error("Authentication error:", error);
  }

  // Wait for UserContext to finish loading before determining provider setup
  if (isLoading) {
    return <AutomotiveLoader />;
  }

  return (
    <BrowserRouter>
      <RoleBasedContextProvider decodedToken={decodedToken}>
        <Content />
      </RoleBasedContextProvider>
    </BrowserRouter>
  );
};

const AppWrapper = () => {
  return (
    <UserProvider>
      <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
        <App />
      </ThemeProvider>
    </UserProvider>
  );
};

export default AppWrapper;
